import { Building2, Server, Layers, Shield, Target, Lightbulb } from "lucide-react";
import { PortalLayout } from "../components/PortalLayout";

export function GroupOverviewPage() {

  return (
    <PortalLayout>

      {/* Header */}
      <div className="mb-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-4">
          Group Overview
        </p>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-[#1F2933] mb-8 leading-tight max-w-3xl">
          A Centralised Technology &amp; Infrastructure Entity
        </h1>
        <div className="space-y-4 text-[#4B5563] max-w-4xl leading-relaxed">
          <p>
            LEO Group Holding Company is a centralised technology and infrastructure holding entity
            focused on the ownership, development, and scaling of digital platforms. The group operates
            through a structured model that integrates enterprise-grade infrastructure, proprietary
            platforms, and strategically aligned partner entities.
          </p>
          <p>
            This approach enables LEO Group to maintain control over its technology stack, intellectual
            property, and operational environments while delivering scalable, secure, and commercially
            viable solutions across multiple sectors. LEO Group is positioned as both an infrastructure
            provider and a platform owner, supporting businesses through secure hosting environments
            while simultaneously developing its own portfolio of digital products.
          </p>
        </div>
      </div>

      {/* Vision & Mission */}
      <div className="grid md:grid-cols-2 gap-6 mb-16">
        <div className="bg-[#FDEAE0]/60 border border-[#7B1A1A]/15 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-6 h-6 text-[#7B1A1A]" />
            <h2 className="text-lg font-semibold text-[#1F2933]">Vision</h2>
          </div>
          <p className="text-[#4B5563] leading-relaxed text-sm">
            To build a scalable, secure, and integrated ecosystem of digital platforms and infrastructure
            that supports modern business operations and long-term value creation.
          </p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="w-6 h-6 text-[#4B5563]" />
            <h2 className="text-lg font-semibold text-[#1F2933]">Mission</h2>
          </div>
          <p className="text-[#4B5563] leading-relaxed text-sm">
            To develop, manage, and scale technology-driven platforms while providing enterprise-grade
            infrastructure services that enable businesses to operate securely, efficiently, and at scale.
          </p>
        </div>
      </div>

      {/* Group Structure */}
      <div className="mb-16">
        <h2 className="text-xl font-semibold text-[#1F2933] mb-8">Group Structure</h2>
        <div className="grid md:grid-cols-3 gap-10">

          <div className="space-y-4">
            <div className="w-full h-px bg-[#7B1A1A]" />
            <Building2 className="w-8 h-8 text-[#7B1A1A]" />
            <h3 className="text-base font-semibold text-[#1F2933]">Owned Platforms</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Proprietary platforms under majority or full ownership and direct strategic control
            </p>
            <ul className="text-sm text-[#4B5563] space-y-1.5">
              {["AutoGuard", "MyCash", "Life Happens", "GetMePrinting"].map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#7B1A1A] inline-block flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <div className="w-full h-px bg-[#1F2933]" />
            <Server className="w-8 h-8 text-[#1F2933]" />
            <h3 className="text-base font-semibold text-[#1F2933]">Infrastructure</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Enterprise hosting services, secure data environments, and scalable infrastructure solutions
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-full h-px bg-[#4B5563]" />
            <Layers className="w-8 h-8 text-[#4B5563]" />
            <h3 className="text-base font-semibold text-[#1F2933]">Partner Ecosystem</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Strategically aligned partner entities, partially or fully owned by LEO Group, operating
              independently to deliver specialised services across development, marketing, and technical
              implementation
            </p>
          </div>

        </div>
      </div>

      {/* Operating Principles */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 mb-14 shadow-sm">
        <h3 className="text-base font-semibold text-[#1F2933] mb-6">Operating Principles</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-[#7B1A1A]" />
              <h4 className="text-sm font-semibold text-[#1F2933]">Ownership Structure</h4>
            </div>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              LEO Group maintains majority or full ownership across its core platforms and selected
              partner entities, ensuring control over intellectual property, strategic direction, and
              long-term value creation.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-[#7B1A1A]" />
              <h4 className="text-sm font-semibold text-[#1F2933]">Strategic Alignment</h4>
            </div>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              While partner entities operate independently, LEO Group retains strategic influence and
              alignment across development, marketing, infrastructure, and operational execution.
            </p>
          </div>
        </div>
      </div>


    </PortalLayout>
  );
}
